// Shared schema.org builders.
//
// Gerdoo's product, marketplace, and pricing pages describe a cloud-hosting
// *service*, not a purchasable physical good or a downloadable app. Emitting
// `Service` (rather than `Product` / `SoftwareApplication`) keeps the markup
// accurate and avoids Google's merchant-listing / software recommendations
// (review, aggregateRating, shippingDetails, hasMerchantReturnPolicy) that don't
// apply to a service — without fabricating any review data.

export interface ServiceSchemaInput {
  name: string;
  description?: string;
  image?: string;
  /** Organization name behind the service. Defaults to گردو کلاد. */
  provider?: string;
  /** Offer / AggregateOffer object; `availability: InStock` is added for Offers. */
  offers?: Record<string, unknown>;
  serviceType?: string;
}

export function serviceSchema({
  name,
  description,
  image,
  provider = "گردو کلاد",
  offers,
  serviceType = "Cloud hosting",
}: ServiceSchemaInput) {
  const offerType = (offers?.["@type"] as string | undefined) ?? "Offer";
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    name,
    ...(image && { image }),
    ...(description && { description }),
    provider: { "@type": "Organization", name: provider },
    areaServed: "IR",
    ...(offers && {
      // A single Offer carries availability; an AggregateOffer (price range)
      // does not take an availability field, so only add it for plain Offers.
      offers: offerType === "Offer" ? { availability: "https://schema.org/InStock", ...offers } : offers,
    }),
  };
}

export interface FAQItemInput {
  q: string;
  a: string;
}

export function faqPageSchema(items: FAQItemInput[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

